import React, {useState} from 'react'
import MemberRepo from '../../../Repo/MemberRepo'
import {useAsync} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {useLocation, useParams} from 'react-router-dom'
import {BucketInfo} from '../../../DTO/BucketInfoDTO'
import {BucketLeave} from '../../../DTO/BucketLeaveDTO'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {validateMemberLeaveBucketInput, ValidationLeaveBucketInput} from '../../../Utils/Validations/ValidateMembersLeaveBucketInput'
import {MBMemberTextArea} from '../../Atoms/MBMemberTextArea'
import {MembersLeaveBucketPageLocationState} from '../../../Props/MembersLeaveBucketPageLocationStates'
import {animateScroll} from 'react-scroll'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {ExternalLinkButton} from '../../Atoms/Buttons'
import {LeavingMessageContent} from '../../Components/LeavingMessageContent'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ProcessingErrorPage} from '../Error/ProcessingErrorPage'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {PIMCard} from '../../Atoms/Cards/PIMCard'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {Spacer} from '../../../../Common/Components/Spacer'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {MembersMobileCarrierMaintenancePage} from '../Error/MembersMobileCarrierMaintenancePage'
import {CvsProcedureOrCompletedErrorPage} from './Components/CvsProcedureOrCompletedErrorPage'
import {Contract} from '../../../Utils/Resources'
import STATUS = Contract.STATUS

type ErrorState = {
  isInputError: boolean
  bucketLeaveReasonErrors: string[]
}

type MembersLeaveBucketInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export const MembersLeaveBucketInputPage: React.VFC<MembersLeaveBucketInputPageProps> = (props) => {
  const location = useLocation<MembersLeaveBucketPageLocationState>()
  const locationState: MembersLeaveBucketPageLocationState = location.state

  const {bucketKey} = useParams<{ bucketKey: string }>()
  const [bucketLeaveReason, setBucketLeaveReason]
    = useState<string>(locationState?.bucketLeaveReason || '')
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    bucketLeaveReasonErrors: [],
  })

  const getBucketInfoByBucketKeyResult = useAsync(async () => {
    return await props.memberRepo.getBucketInfoByBucketKey(bucketKey)
  })

  const getContractInfoByBucketKeyResult = useAsync(async () => {
    return await props.memberRepo.getContractInfoByBucketKey(bucketKey)
  })

  const getBucketLeaveByBucketKeyResult = useAsync(async () => {
    return await props.memberRepo.getBucketLeaveByBucketKey(bucketKey)
  })

  if (getBucketInfoByBucketKeyResult.loading) {
    return null
  }

  if (getBucketInfoByBucketKeyResult.error) {
    throw new MBMemberError(getBucketInfoByBucketKeyResult.error.message)
  }

  if (!getBucketInfoByBucketKeyResult.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(getBucketInfoByBucketKeyResult.value.status)

  if (!getBucketInfoByBucketKeyResult.value.data) {
    throw new MBMemberError('SystemError')
  }

  const _bucketInfo = {...getBucketInfoByBucketKeyResult.value.data}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {mailMagazineList, ...rest} = _bucketInfo
  const bucketInfo: Omit<BucketInfo, 'mailMagazineList'> = rest
  const pageTitle = `${bucketInfo.name} 退会`

  if (getContractInfoByBucketKeyResult.loading) {
    return null
  }

  if (getContractInfoByBucketKeyResult.error) {
    throw new MBMemberError(getContractInfoByBucketKeyResult.error.message)
  }

  if (!getContractInfoByBucketKeyResult.value) {
    throw new MBMemberError('UnknownError')
  }

  switch (getContractInfoByBucketKeyResult.value.status) {
    case CommonResponseStatus.Ok:
      break
    case CommonResponseStatus.NotFound:
      throw new MBMemberError('NotFound')
    case CommonResponseStatus.InternalServerError:
      throw new MBMemberError('ServerError')
    case CommonResponseStatus.NoAuthority:
      throw new MBMemberError('SystemError')
    case CommonResponseStatus.Processing:
      return (
        <ProcessingErrorPage
          pageTitle={pageTitle}
          bucketInfo={{...getBucketInfoByBucketKeyResult.value.data}}
          buttonName={getBucketInfoByBucketKeyResult.value.data.fcMyPageInfo.name}
          url={getBucketInfoByBucketKeyResult.value.data.fcMyPageInfo.url}
        />
      )
    default:
      throw new MBMemberError('UnknownError')
  }

  if (!getContractInfoByBucketKeyResult.value.data) {
    throw new MBMemberError('UnknownError')
  }

  const {now, next} = getContractInfoByBucketKeyResult.value.data

  if (getBucketLeaveByBucketKeyResult.loading) {
    return null
  }

  if (getBucketLeaveByBucketKeyResult.error) {
    throw new MBMemberError(getBucketLeaveByBucketKeyResult.error.message)
  }

  if (!getBucketLeaveByBucketKeyResult.value) {
    throw new MBMemberError('UnknownError')
  }

  switch (getBucketLeaveByBucketKeyResult.value.status) {
    case CommonResponseStatus.Ok:
      break
    case CommonResponseStatus.NotFound:
      throw new MBMemberError('NotFound')
    case CommonResponseStatus.OvertimeError:
      return (
        <MembersMobileCarrierMaintenancePage
          pageTitle={pageTitle}
          operationName={'退会手続き'}
          bucketInfo={bucketInfo}
          buttonName={bucketInfo.fcMyPageInfo.name}
          url={bucketInfo.fcMyPageInfo.url}
        />
      )
    case CommonResponseStatus.Forbidden:
      if (getBucketLeaveByBucketKeyResult.value.errorMessageList.length === 2) {
        const errorMessageList = getBucketLeaveByBucketKeyResult.value.errorMessageList
        return (
          <CvsProcedureOrCompletedErrorPage
            bucketInfo={bucketInfo}
            pageTitle={pageTitle}
            errorSummaryHeadLine={errorMessageList[0]}
            errorSummaryDetail={errorMessageList[1]}
          />
        )
      }
      throw new MBMemberError('NotFound')
    case CommonResponseStatus.InternalServerError:
      throw new MBMemberError('ServerError')
    case CommonResponseStatus.NoAuthority:
      throw new MBMemberError('SystemError')
    case CommonResponseStatus.Processing:
      if (now.contractMethod.method === 'none') {
        return (
          <LeavingMessageContent
            pageTitle={pageTitle}
            bucketInfo={bucketInfo}
          />
        )
      }
      throw new MBMemberError('SystemError')
    default:
      throw new MBMemberError('UnknownError')
  }

  if (!getBucketLeaveByBucketKeyResult.value.data) {
    throw new MBMemberError('SystemError')
  }
  const bucketLeave: BucketLeave =
    {...getBucketLeaveByBucketKeyResult.value.data}

  if (now.contractMethod.method !== 'none' && next === null) {
    throw new MBMemberError('SystemError')
  }

  if (next?.status === STATUS.Canceled) {
    return (
      <LeavingMessageContent
        pageTitle={pageTitle}
        bucketInfo={bucketInfo}
      />
    )
  }

  const {isInputError, bucketLeaveReasonErrors} = errorState

  const goToMembersLeaveBucketConfirmPage = (): void => {
    const inputForValidation: ValidationLeaveBucketInput = {
      bucketLeaveReason: bucketLeaveReason,
    }

    const validationResult = validateMemberLeaveBucketInput(
      inputForValidation,
    )

    if (validationResult.hasError) {
      setErrorState({
        isInputError: true,
        bucketLeaveReasonErrors: validationResult.errors.bucketLeaveReason,
      })
      animateScroll.scrollToTop()
      return
    }

    props.navigator.goToMembersLeaveBucketConfirmPage({
      bucketInfo: bucketInfo,
      bucketLeave: bucketLeave,
      bucketLeaveReason: bucketLeaveReason,
      nowContract: now,
    })
  }

  return (
    <MembersPageTemplate
      hideFooterNavi={true}
      bucketInfo={bucketInfo}
    >
      <PageTitle>{pageTitle}</PageTitle>

      <InputErrorSummary display={isInputError}/>

      <StepGuide activity={'leave'} current={'enter'}/>

      <PIMStack alignItems={'center'}>
        <PIMCard
          headerProps={{title: '退会のご案内'}}
          testId={'block-leave-form'}
        >
          <PIMStack spacing={2} divider={<MBContentBodyDivider/>}>
            <PIMStack spacing={'10px'}>
              <MBContentSubTitleTypography>
                {bucketLeave?.leavePageTopMessage}
              </MBContentSubTitleTypography>
              <MBContentNoteWithMarkTypography
                note={
                  now.contractMethod.method === 'none' ?
                    `本日付で${bucketInfo.name}退会となります。`
                    :
                    `現在の契約期間満了後に${bucketInfo.name}退会となります。`
                }
              />
            </PIMStack>
            <PIMStack spacing={'10px'}>
              <label htmlFor="leave-reason">
                <MBContentSubTitleTypography>
                  退会理由
                </MBContentSubTitleTypography>
              </label>
              <Spacer mb={'18px'}>
                <MBMemberTextArea
                  className="form-textarea_01 w_100 pc100 mb_0"
                  name="bucketLeaveReason"
                  id="leave-reason"
                  value={bucketLeaveReason}
                  rows={10}
                  hasError={bucketLeaveReasonErrors.length > 0}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
                    setBucketLeaveReason(e.target.value)
                  }}
                >
                </MBMemberTextArea>
                <ErrorDisplay errors={bucketLeaveReasonErrors}/>
              </Spacer>
            </PIMStack>
          </PIMStack>
        </PIMCard>
      </PIMStack>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>
        『退会する』ボタンを押してください。
      </MessageForAction>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'withdraw-button'}
          onClick={(): void => {
            goToMembersLeaveBucketConfirmPage()
          }}
        >
          退会する
        </PIMSubmitButton>
        <ExternalLinkButton
          label={`${getBucketInfoByBucketKeyResult.value?.data?.fcMyPageInfo.name ?? ''}に移動する`}
          url={getBucketInfoByBucketKeyResult.value?.data?.fcMyPageInfo.url}
        />
      </PIMPageActions>
    </MembersPageTemplate>
  )
}
